import { useState } from "react";
import { Link } from "react-router-dom";
import {
    Card, CardContent, CardHeader,
    CardTitle, CardDescription
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, Sparkles, ArrowLeft, CheckCircle2 } from "lucide-react";
import { apiClient } from "@/services/apiClient";
import { API_URLS } from "@/constants/urls";
import { apiErrorHandler } from "@/helpers/apiErrorHandler";

export default function ForgotPasswordPage() {
    const [email, setEmail] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isEmailSent, setIsEmailSent] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!email) {
            toast.error("Please enter your email address");
            return;
        }

        setIsLoading(true);

        try {
            const result = await apiClient.post(API_URLS.FORGOT_PASSWORD, { email });

            if (result.status === 200) {
                setIsEmailSent(true);
            }
        } catch (error) {
            apiErrorHandler(error);
        }

        setIsLoading(false);
    };

    return (
        <div className="min-h-screen flex">

            {/* Left Side — same as Register/Login */}
            <div className="hidden lg:flex lg:w-1/2 hero-gradient relative overflow-hidden">
                <div className="relative z-10 flex flex-col justify-center px-12 lg:px-16">

                    <div className="flex items-center gap-3 mb-12">
                        <div className="w-12 h-12 rounded-xl bg-primary-foreground/10 backdrop-blur-sm flex items-center justify-center font-bold text-xl text-primary-foreground border border-primary-foreground/20">
                            P
                        </div>
                        <span className="font-semibold text-2xl text-primary-foreground">
                            Prowen
                        </span>
                    </div>

                    <h1 className="text-4xl lg:text-5xl font-bold text-primary-foreground mb-6 leading-tight">
                        Make Smarter<br />
                        <span className="text-accent-light">Pricing Decisions</span>
                    </h1>

                    <p className="text-lg text-primary-foreground/80 mb-8 max-w-md">
                        Real-time competitor tracking, review monitoring, and market trend analysis for modern hotel portfolios.
                    </p>

                    <div className="space-y-4">
                        {[
                            "Competitor Rate Tracking",
                            "Review & Rankings Monitoring",
                            "Real-Time Market Trends",
                            "AI-Powered Insights",
                        ].map((feature, index) => (
                            <div key={index} className="flex items-center gap-3 text-primary-foreground/80">
                                <div className="w-5 h-5 rounded-full bg-accent/20 flex items-center justify-center">
                                    <Sparkles className="w-3 h-3 text-accent" />
                                </div>
                                <span>{feature}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="absolute bottom-0 right-0 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
                <div className="absolute top-20 right-20 w-64 h-64 bg-primary-light/10 rounded-full blur-3xl" />
            </div>

            {/* Right Side */}
            <div className="flex-1 flex items-center justify-center p-8 bg-background">
                <div className="w-full max-w-md">

                    {/* Mobile Logo */}
                    <div className="flex items-center gap-3 mb-8 lg:hidden">
                        <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center font-bold text-primary-foreground">
                            P
                        </div>
                        <span className="font-semibold text-xl text-foreground">
                            Prowen
                        </span>
                    </div>

                    <Card className="border-0 shadow-xl">
                        <CardHeader className="space-y-1 pb-6">
                            <CardTitle className="text-2xl">Forgot Password</CardTitle>
                            <CardDescription>
                                {isEmailSent
                                    ? "Check your inbox for the reset link"
                                    : "Enter your email and we'll send you a reset link"}
                            </CardDescription>
                        </CardHeader>

                        <CardContent>
                            {isEmailSent ? (
                                /* Success State */
                                <div className="flex flex-col items-center text-center space-y-4 py-4">
                                    <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                                        <CheckCircle2 className="w-8 h-8 text-green-600" />
                                    </div>
                                    <div className="space-y-1">
                                        <p className="font-medium text-foreground">Email sent successfully!</p>
                                        <p className="text-sm text-muted-foreground">
                                            We've sent a password reset link to{" "}
                                            <span className="font-medium text-foreground">{email}</span>.
                                            Please check your inbox and follow the instructions.
                                        </p>
                                    </div>
                                    <p className="text-xs text-muted-foreground pt-2">
                                        Didn't receive an email?{" "}
                                        <button
                                            onClick={() => setIsEmailSent(false)}
                                            className="text-primary hover:underline font-medium"
                                        >
                                            Try again
                                        </button>
                                    </p>
                                </div>
                            ) : (
                                /* Form State */
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="email">Email Address</Label>
                                        <div className="relative">
                                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                            <Input
                                                id="email"
                                                name="email"
                                                type="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                placeholder="Enter your email"
                                                className="pl-10"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <Button
                                        type="submit"
                                        className="w-full"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? "Sending reset link..." : "Send Reset Link"}
                                    </Button>
                                </form>
                            )}

                            <div className="mt-6 flex justify-center">
                                <Link
                                    to="/login"
                                    className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    <ArrowLeft className="w-4 h-4" />
                                    Back to Sign In
                                </Link>
                            </div>
                        </CardContent>
                    </Card>

                    <p className="text-center text-xs text-muted-foreground mt-6">
                        © {new Date().getFullYear()} Prowen Technologies India Private Limited
                    </p>
                </div>
            </div>
        </div>
    );
}